import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
  DollarSign,
  Eye,
  Grid3X3,
  CalendarDays
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import sessionsService, { Session } from '../../services/sessions.service';
import { authService } from '../../services/auth.service';
import { formatTrainingLocation } from '../../utils/address';
import { Training } from '../../services/trainings.service';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredSession, setHoveredSession] = useState<Session | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      // Get training center info first
      const trainingCenterInfo = await authService.getTrainingCenterInfo();
      
      // Fetch sessions for this training center
      const data = await sessionsService.getSessionsByTrainingCenter(trainingCenterInfo.id);
      setSessions(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const getSessionsForDate = (date: Date) => {
    // Format date as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    return sessions.filter(session => 
      session.session_date === dateString
    );
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push(date);
    }
    return weekDays;
  };

  const getMonthDays = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      days.push(date);
    }
    return days;
  };

  const days = viewMode === 'week' ? getWeekDays() : getMonthDays();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary via-brand-primary/90 to-brand-primary/80 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold">
                Mon Calendrier
              </h1>
            </div>
            <p className="text-white/90 text-base sm:text-lg">
              Visualisez vos sessions de formation dans un calendrier interactif
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-6 lg:mt-0">
             {/* View Mode Toggle */}
             <div className="flex bg-white/20 backdrop-blur-sm rounded-xl p-1 w-full sm:w-auto">
               <button
                 onClick={() => setViewMode('month')}
                 className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 ${
                   viewMode === 'month'
                     ? 'bg-white text-brand-primary shadow-lg'
                     : 'text-white hover:bg-white/10'
                 }`}
               >
                 <Grid3X3 className="h-3 w-3 sm:h-4 sm:w-4" />
                 <span className="hidden sm:inline">Mois</span>
                 <span className="sm:hidden">M</span>
               </button>
               <button
                 onClick={() => setViewMode('week')}
                 className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 ${
                   viewMode === 'week'
                     ? 'bg-white text-brand-primary shadow-lg'
                     : 'text-white hover:bg-white/10'
                 }`}
               >
                 <CalendarDays className="h-3 w-3 sm:h-4 sm:w-4" />
                 <span className="hidden sm:inline">Semaine</span>
                 <span className="sm:hidden">S</span>
               </button>
             </div>
             <button
               onClick={goToToday}
               className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-brand-primary bg-white rounded-xl hover:bg-brand-primary/10 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
             >
               Aujourd'hui
             </button>
           </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={goToPreviousMonth}
            className="p-3 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-xl transition-all duration-200 group"
          >
            <ChevronLeft className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
          
          <h2 className="text-2xl font-bold text-gray-900 capitalize bg-gradient-to-r from-brand-primary to-brand-primary/80 bg-clip-text text-transparent">
            {formatDate(currentDate)}
          </h2>
          
          <button
            onClick={goToNextMonth}
            className="p-3 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 rounded-xl transition-all duration-200 group"
          >
            <ChevronRight className="h-6 w-6 group-hover:scale-110 transition-transform" />
          </button>
        </div>

                 {/* Calendar Grid */}
         <div className={`grid grid-cols-7 gap-2 ${viewMode === 'week' ? 'min-h-[600px]' : ''}`}>
           {/* Day Headers */}
           {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
             <div key={day} className="p-4 text-center text-sm font-bold text-brand-primary bg-brand-primary/10 rounded-xl">
               {day}
             </div>
           ))}

           {/* Calendar Days */}
           {days.map((date, index) => {
             if (!date) {
               return <div key={index} className={`p-4 bg-gray-50 rounded-xl ${viewMode === 'week' ? 'min-h-[600px]' : 'min-h-[140px]'}`}></div>;
             }

             const daySessions = getSessionsForDate(date);
             const isToday = date.toDateString() === new Date().toDateString();
             const isCurrentMonth = viewMode === 'month' ? date.getMonth() === currentDate.getMonth() : true;

             return (
               <div
                 key={index}
                 className={`p-4 border-2 rounded-xl relative transition-all duration-200 hover:shadow-lg ${
                   viewMode === 'week' ? 'min-h-[600px]' : 'min-h-[140px]'
                 } ${
                   isToday 
                     ? 'bg-gradient-to-br from-emerald-500 to-brand-primary border-brand-primary/40 shadow-lg' 
                     : 'bg-white border-gray-100 hover:border-brand-primary/20'
                 } ${viewMode === 'month' && !isCurrentMonth ? 'opacity-40' : ''}`}
               >
                 {/* Day Number */}
                 <div className={`text-lg font-bold mb-3 ${
                   isToday ? 'text-white' : 'text-gray-900'
                 }`}>
                   {date.getDate()}
                 </div>

                 {/* Session Events */}
                 <div className="space-y-2">
                   {daySessions.slice(0, viewMode === 'week' ? 8 : 2).map((session) => (
                     <div
                       key={session.id}
                       className="relative group"
                       onMouseEnter={() => setHoveredSession(session)}
                       onMouseLeave={() => setHoveredSession(null)}
                     >
                       <button
                         onClick={() => navigate(`/training-center/trainings/${session.training.id}`)}
                         className={`w-full text-left p-3 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer transform hover:scale-105 ${
                           isToday
                             ? 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                             : 'bg-gradient-to-r from-brand-primary/20 to-brand-primary/20 text-emerald-800 hover:from-brand-primary/20 hover:to-brand-primary/30'
                         }`}
                       >
                         <div className="truncate font-medium">{session.training.title}</div>
                         <div className={`text-xs mt-1 ${
                           isToday ? 'text-white/90' : 'text-brand-primary'
                         }`}>
                           {session.training.format} • {session.training.pricing_dzd} DZD
                         </div>
                       </button>

                       {/* Hover Tooltip */}
                       {hoveredSession?.id === session.id && (
                         <motion.div
                           initial={{ opacity: 0, y: 10, scale: 0.95 }}
                           animate={{ opacity: 1, y: 0, scale: 1 }}
                           exit={{ opacity: 0, y: 10, scale: 0.95 }}
                           className="absolute z-50 left-full ml-3 top-0 w-80 bg-white border-2 border-brand-primary/20 rounded-2xl shadow-2xl p-6"
                         >
                           <div className="space-y-4">
                             <div>
                               <h4 className="font-bold text-gray-900 text-sm">
                                 {session.training.title}
                               </h4>
                               {session.training.domaine && (
                                 <p className="text-xs text-brand-primary font-medium">
                                   {session.training.domaine.name_french}
                                 </p>
                               )}
                               <p className="text-xs text-gray-500 font-medium">
                                 Session du {new Date(session.session_date).toLocaleDateString('fr-FR')}
                               </p>
                             </div>

                             <div className="space-y-3">
                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-brand-primary/20 rounded-lg">
                                   <Clock className="h-3 w-3 text-brand-primary" />
                                 </div>
                                 <span className="font-medium">{session.training.duration}</span>
                               </div>
                               
                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-brand-primary/20 rounded-lg">
                                   <MapPin className="h-3 w-3 text-brand-primary" />
                                 </div>
                                 <span className="font-medium">
                                   {formatTrainingLocation(session.training as unknown as Training)}
                                 </span>
                               </div>

                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-brand-primary/20 rounded-lg">
                                   <DollarSign className="h-3 w-3 text-brand-primary" />
                                 </div>
                                 <span className="font-medium">{session.training.pricing_dzd} DZD</span>
                               </div>

                               <div className="flex items-center gap-3 text-xs text-gray-600">
                                 <div className="p-1.5 bg-brand-primary/20 rounded-lg">
                                   <Users className="h-3 w-3 text-brand-primary" />
                                 </div>
                                 <span className="font-medium">Format: {session.training.format}</span>
                               </div>
                             </div>

                             <button
                               onClick={() => navigate(`/training-center/trainings/${session.training.id}`)}
                               className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-primary/90 text-white text-sm font-bold rounded-xl hover:from-brand-primary/90 hover:to-brand-primary/80 transition-all duration-200 transform hover:scale-105 shadow-lg"
                             >
                               <Eye className="h-4 w-4" />
                               Voir détails
                             </button>
                           </div>
                         </motion.div>
                       )}
                     </div>
                   ))}

                   {daySessions.length > (viewMode === 'week' ? 8 : 2) && (
                     <div className={`text-xs text-center font-medium ${
                       isToday ? 'text-white/90' : 'text-brand-primary'
                     }`}>
                       +{daySessions.length - (viewMode === 'week' ? 8 : 2)} autres
                     </div>
                   )}
                 </div>
               </div>
             );
           })}
         </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-brand-primary/20 rounded-lg">
            <CalendarIcon className="h-5 w-5 text-brand-primary" />
          </div>
          Légende
        </h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gradient-to-r from-brand-primary/20 to-brand-primary/20 rounded-lg border-2 border-brand-primary/30"></div>
            <span className="text-sm font-medium text-gray-700">Session de formation</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-brand-primary rounded-lg border-2 border-brand-primary/40"></div>
            <span className="text-sm font-medium text-gray-700">Aujourd'hui</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gray-100 rounded-lg border-2 border-gray-200"></div>
            <span className="text-sm font-medium text-gray-700">Jour vide</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
